import { Badge, Button, DrawerContent, DrawerHeader, DrawerRoot, DrawerTitle, Tooltip } from '@ibsys/kit';
import { CopyIcon, RefreshCwIcon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RunTaskDetail, useRestartRun } from '@/entities/jobs';
import { formatTimeInterval, withSecondsLabel } from '@/shared/lib/utils/string';
import { useTimeDisplayFormat } from '@/entities/settings';
import { CopyButton } from '@/shared/ui';
import { JsonBlock } from './JsonBlock';
import { buildTaskMarkdownReport, TASK_STATUS_VARIANTS } from './utils';

type TaskDetailDrawerProps = {
  task: RunTaskDetail | null;
  runId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

// Копирование текста в буфер обмена
const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

export const TaskDetailDrawer: FC<TaskDetailDrawerProps> = ({ task, runId, open, onOpenChange }) => {
  const { t } = useTranslation(['runs', 'common']);
  const timeDisplayFormat = useTimeDisplayFormat();
  const restartMutation = useRestartRun();

  const handleRestartFromTask = useCallback(() => {
    if (!task) return;
    restartMutation.mutate(
      { runId, payload: { mode: 'from_step', stepIndex: task.orderIndex } },
      { onSuccess: () => onOpenChange(false) },
    );
  }, [task, runId, restartMutation, onOpenChange]);

  const markdownReport = useMemo(() => {
    if (!task) return '';
    return buildTaskMarkdownReport(task, runId, t, timeDisplayFormat);
  }, [task, runId, t, timeDisplayFormat]);

  if (!task) return null;

  return (
    <DrawerRoot open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="m-4 ml-0 rounded-2xl">
        <DrawerHeader>
          <div className="flex items-center gap-1">
            <DrawerTitle>{t('runs:taskDrawer.title')}</DrawerTitle>
            <CopyButton text={markdownReport} tooltip={t('runs:taskDrawer.copyAll')} />
          </div>
        </DrawerHeader>

        <div className="flex flex-col gap-4 overflow-auto p-6 pt-0">
          {/* Основная информация */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1">
              <span className="text-xs font-mono text-muted-foreground">{runId}</span>
              <Tooltip tooltip={t('runs:taskDrawer.copy')}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-5"
                  aria-label={t('runs:taskDrawer.copy')}
                  onClick={() => copyToClipboard(runId)}
                >
                  <CopyIcon className="size-3" />
                </Button>
              </Tooltip>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-xs font-mono text-muted-foreground">{String(task.id)}</span>
              <Tooltip tooltip={t('runs:taskDrawer.copy')}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-5"
                  aria-label={t('runs:taskDrawer.copy')}
                  onClick={() => copyToClipboard(String(task.id))}
                >
                  <CopyIcon className="size-3" />
                </Button>
              </Tooltip>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('runs:taskDrawer.pipeline')}</span>
              <span className="text-sm font-mono">{task.pipelineId}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('runs:taskDrawer.taskId')}</span>
              <span className="text-sm font-mono">{task.taskId}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('runs:taskDrawer.status')}</span>
              <Badge variant={TASK_STATUS_VARIANTS[task.status]} outline>
                {t(`runs:taskStatus.${task.status}`)}
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('runs:taskDrawer.attempt')}</span>
              <span className="text-sm">{task.attempt}</span>
            </div>

            {task.duration !== null && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {withSecondsLabel(t('runs:taskDrawer.duration'), timeDisplayFormat, t('common:secondsSuffix'))}
                </span>
                <span className="text-sm">{formatTimeInterval(task.duration, timeDisplayFormat)}</span>
              </div>
            )}

            {task.progress !== null && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('runs:taskDrawer.progress')}</span>
                <span className="text-sm">{`${task.progress}%`}</span>
              </div>
            )}
          </div>

          {/* Ошибка */}
          {task.error && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <h4 className="text-sm font-medium text-destructive">{t('runs:taskDrawer.error')}</h4>
                <CopyButton
                  text={`${task.error.errorType}: ${task.error.message}`}
                  tooltip={t('runs:taskDrawer.copy')}
                />
              </div>
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t('runs:taskDrawer.errorType')}</span>
                  <span className="text-xs font-mono">{task.error.errorType}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground">{t('runs:taskDrawer.errorMessage')}</span>
                  <p className="text-xs">{task.error.message}</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t('runs:taskDrawer.willRetry')}</span>
                  <span className="text-xs">
                    {task.error.willRetry ? t('runs:taskDrawer.yes') : t('runs:taskDrawer.no')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Входные данные */}
          <JsonBlock
            title={t('runs:taskDrawer.inputData')}
            data={task.params}
            emptyLabel={t('runs:taskDrawer.noData')}
            copyTooltip={t('runs:taskDrawer.copy')}
          />

          {/* Исходящие данные */}
          <JsonBlock
            title={t('runs:taskDrawer.outputData')}
            data={task.results}
            emptyLabel={t('runs:taskDrawer.noData')}
            copyTooltip={t('runs:taskDrawer.copy')}
          />

          {/* Кнопка перезапуска */}
          <Button onClick={handleRestartFromTask} disabled={restartMutation.isPending} className="w-full">
            <RefreshCwIcon className="size-4 mr-2" />
            {restartMutation.isPending ? t('runs:taskDrawer.restartingFromTask') : t('runs:taskDrawer.restartFromTask')}
          </Button>
        </div>
      </DrawerContent>
    </DrawerRoot>
  );
};
